import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, toastError } from "../../lib/api";
import { PageHeader } from "../../components/domain/Bits";
import { Building2, CircleAlert, Clock3, Shield, UsersRound } from "lucide-react";

type Tenant = {
  id: string; name: string; slug: string; status: string;
  default_timezone?: string; applied_template?: string | null; created_at?: string;
};
type SupportSession = { id: string; tenant_id: string; expires_at: string; reason: string };

function Metric({ label, value, note, icon: Icon }: { label: string; value: number; note: string; icon: React.ElementType }) {
  return <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center justify-between"><span className="text-sm text-slate-500">{label}</span><Icon className="h-5 w-5 text-slate-400" /></div>
    <div className="mt-2 text-3xl font-semibold tracking-tight">{value}</div>
    <div className="mt-1 text-xs text-slate-500">{note}</div>
  </div>;
}

export default function PlatformDashboard() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [sessions, setSessions] = useState<SupportSession[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/platform/tenants"), api.get("/support-access/active")])
      .then(([tenantResponse, sessionResponse]) => {
        setTenants(tenantResponse.data);
        setSessions(sessionResponse.data);
      })
      .catch(toastError)
      .finally(() => setLoading(false));
  }, []);

  const active = tenants.filter((tenant) => tenant.status === "active");
  const suspended = tenants.filter((tenant) => tenant.status !== "active");
  const alerts = useMemo(() => [
    ...suspended.map((tenant) => ({ id: `suspended-${tenant.id}`, tenant, text: "Tenant is suspended" })),
    ...active.filter((tenant) => !tenant.applied_template).map((tenant) => ({ id: `template-${tenant.id}`, tenant, text: "No CRM template has been applied" })),
    ...active.filter((tenant) => !tenant.default_timezone || tenant.default_timezone === "UTC").map((tenant) => ({ id: `timezone-${tenant.id}`, tenant, text: "Business timezone still uses UTC" })),
  ], [tenants]);

  return <>
    <PageHeader title="Platform Control Center" subtitle="Executive oversight for every RemediKeep tenant" />
    <div className="mx-auto max-w-7xl space-y-6 p-6 md:p-8">
      {loading ? <div className="text-sm text-slate-500">Loading platform controls…</div> : <>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Metric label="Total tenants" value={tenants.length} note="All client workspaces" icon={Building2} />
          <Metric label="Active tenants" value={active.length} note="Currently operational" icon={UsersRound} />
          <Metric label="Suspended tenants" value={suspended.length} note="Access disabled" icon={CircleAlert} />
          <Metric label="Support sessions" value={sessions.length} note="Active audited access" icon={Shield} />
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <section className="rounded-xl border border-slate-200 bg-white lg:col-span-2">
            <div className="flex items-center justify-between border-b border-slate-200 p-5">
              <div><h2 className="font-semibold">Tenant portfolio</h2><p className="text-sm text-slate-500">Status, template, and business timezone at a glance</p></div>
              <Link to="/platform/tenants" className="text-sm font-medium text-slate-700 hover:underline">Manage tenants</Link>
            </div>
            <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="platform-tenant-portfolio">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500"><tr><th className="px-5 py-3 text-left">Tenant</th><th className="px-5 py-3 text-left">Status</th><th className="px-5 py-3 text-left">Template</th><th className="px-5 py-3 text-left">Timezone</th></tr></thead>
              <tbody>{tenants.map((tenant) => <tr key={tenant.id} className="border-t border-slate-100">
                <td className="px-5 py-3"><Link to={`/platform/tenants/${tenant.id}`} className="font-medium hover:underline">{tenant.name}</Link><div className="text-xs text-slate-500">{tenant.slug}</div></td>
                <td className="px-5 py-3 capitalize">{tenant.status}</td>
                <td className="px-5 py-3">{tenant.applied_template || "Not configured"}</td>
                <td className="px-5 py-3">{tenant.default_timezone || "UTC"}</td>
              </tr>)}</tbody>
            </table></div>
          </section>

          <section className="rounded-xl border border-slate-200 bg-white">
            <div className="border-b border-slate-200 p-5"><h2 className="font-semibold">Needs attention</h2><p className="text-sm text-slate-500">Configuration and lifecycle exceptions</p></div>
            <div className="divide-y divide-slate-100" data-testid="platform-alerts">
              {alerts.length === 0 ? <div className="p-5 text-sm text-emerald-700">No platform alerts.</div> : alerts.slice(0, 8).map((alert) => <div key={alert.id} className="p-4">
                <div className="text-sm font-medium">{alert.tenant.name}</div><div className="mt-1 text-xs text-amber-700">{alert.text}</div>
              </div>)}
            </div>
          </section>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <Link to="/platform/tenants" className="rounded-xl border border-slate-200 bg-white p-5 hover:border-slate-400"><Building2 className="h-5 w-5" /><div className="mt-3 font-semibold">Tenant lifecycle</div><div className="mt-1 text-sm text-slate-500">Create, configure, suspend, reactivate, and provision Owners.</div></Link>
          <Link to="/platform/support-access" className="rounded-xl border border-slate-200 bg-white p-5 hover:border-slate-400"><Shield className="h-5 w-5" /><div className="mt-3 font-semibold">Support access</div><div className="mt-1 text-sm text-slate-500">Enter a tenant through audited, expiring support sessions.</div></Link>
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5"><Clock3 className="h-5 w-5 text-slate-400" /><div className="mt-3 font-semibold text-slate-700">Next control slice</div><div className="mt-1 text-sm text-slate-500">Plans, modules, billing status, and usage limits.</div></div>
        </div>
      </>}
    </div>
  </>;
}
